import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { aiAnalysisService } from '../services/aiAnalysisService';
import type { Trade, AIAnalysis } from '../types';

interface AIAnalysisContextType {
  analysis: AIAnalysis | null;
  loading: boolean;
  error: string | null;
  analyze: (trade: Trade) => Promise<void>;
  isAnalyzing: boolean;
  setIsAnalyzing: (value: boolean) => void;
  currentAnalysis: AIAnalysis | null;
  setCurrentAnalysis: (analysis: AIAnalysis | null) => void;
  hasVisitedAnalysis: boolean;
  setHasVisitedAnalysis: (visited: boolean) => void;
  tagFilterBanner: string | null;
  setTagFilterBanner: (message: string | null) => void;
}

const AIAnalysisContext = createContext<AIAnalysisContextType | undefined>(undefined);

export const AIAnalysisProvider = ({ children }: { children: ReactNode }) => {
  const [analysis, setAnalysis] = useState<AIAnalysis | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [currentAnalysis, setCurrentAnalysis] = useState<AIAnalysis | null>(null);
  const [hasVisitedAnalysis, setHasVisitedAnalysis] = useState(false);
  const [tagFilterBanner, setTagFilterBanner] = useState<string | null>(null);

  const analyze = async (trade: Trade) => {
    try {
      setLoading(true);
      setError(null);
      const result = await aiAnalysisService.analyzeTrade(trade);
      setAnalysis(result);
    } catch (error: any) {
      // console.error supprimé
      setError(error.message || 'Failed to analyze trade');
    } finally {
      setLoading(false);
    }
  };

  const value = {
    analysis,
    loading,
    error,
    analyze,
    isAnalyzing,
    setIsAnalyzing,
    currentAnalysis,
    setCurrentAnalysis,
    hasVisitedAnalysis,
    setHasVisitedAnalysis,
    tagFilterBanner,
    setTagFilterBanner
  };

  return (
    <AIAnalysisContext.Provider value={value}>
      {children}
    </AIAnalysisContext.Provider>
  );
};

export const useAIAnalysis = () => {
  const context = useContext(AIAnalysisContext);
  if (context === undefined) {
    throw new Error('useAIAnalysis must be used within an AIAnalysisProvider');
  }
  return context;
};

